import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { Chip } from '@crowdex/ui/components/chip';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { Input } from '@crowdex/ui/components/input';
import { toast } from '@crowdex/ui/components/toaster';
import { ArrowUpRight, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { artifactGlyph, hasPreview, LibraryCard } from '@/components/library-page/library-card';
import {
  appCategoryKeys,
  appNoun,
  categoryOf,
  libraryArtifacts,
  libraryCategories,
  pluginArtifacts,
  pluginGroups,
} from '@/components/library-page/library-model';
import {
  LIBRARY_GRID,
  LibrarySection,
  PluginGroups,
} from '@/components/library-page/library-plugin-section';
import { duplicateArtifact } from '@/features/apps/app-conversation';
import { artifactMatches } from '@/features/apps/artifact-grid';
import { withRenders } from '@/features/apps/renders';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import { type PluginWorkbench, railPanels } from '@/features/plugins/plugin-workbench';
import {
  PluginWorkbenchContext,
  usePluginWorkbench,
} from '@/features/plugins/use-plugin-workbench';
import { appHomePath, LIBRARY_PATH, openAppHome } from '@/lib/app-home';
import { applyAppSettings, readAppSettings, resolveDesktopTheme } from '@/lib/app-settings';
import { useWorkspaceDirectory } from '@/lib/assistants/directory';
import { useRouter } from '@/lib/navigation';

/** The pill that shows only what the plugins provide. */
const FROM_PLUGINS = 'from-plugins';

/**
 * The Library (D26): the user's artifacts first, then what their plugins provide (templates,
 * design systems…) by app, each app collapsed; a filter pill per kind, across both, and one for
 * the plugins' alone. `/artifacts?app=` only filters on an app's kinds, with a link to the app's
 * home (an app's own place, docs/redesign-implementation-plan.md §6.1). An artifact opens in its
 * type's app (its page on it); a file no app shows, in the conversation that made it. "Use" of
 * what a plugin ships is its Duplicate: the copy opens in a new conversation.
 */
export function ArtifactsPage({
  initialArtifactId = null,
  app = null,
  workbench: given,
  artifacts: givenArtifacts,
}: {
  initialArtifactId?: string | null;
  /** The app whose artifacts the page shows (its plugin name). */
  app?: string | null;
  /** The apps, when the page does not read them itself (the preview). */
  workbench?: PluginWorkbench;
  /** The artifacts, when the page does not read the index itself (the preview). */
  artifacts?: readonly ArtifactListingEntry[];
}) {
  const router = useRouter();
  const [theme, setTheme] = useState<'desktop-light' | 'desktop-dark'>('desktop-dark');
  const [query, setQuery] = useState('');
  const [requested, setRequested] = useState<string | null>(initialArtifactId);
  const [using, setUsing] = useState<string | null>(null);
  const loaded = usePluginWorkbench(null, given === undefined);
  const workbench = given ?? loaded;
  const read = useArtifactIndex(undefined, { enabled: givenArtifacts === undefined });
  const index =
    givenArtifacts === undefined
      ? read
      : { ...read, artifacts: givenArtifacts, loading: false, error: null };

  const panel = useMemo(
    () =>
      app === null
        ? null
        : (railPanels(workbench).find(
            (candidate) => candidate.plugin === app && candidate.appView !== undefined,
          ) ?? null),
    [app, workbench],
  );
  const appKeys = useMemo(() => (panel === null ? [] : appCategoryKeys(panel)), [panel]);
  const appKey = appKeys[0] ?? null;
  const [filter, setFilter] = useState<string | null>(null);
  // The app's tile selects its kind; the Library's shows all, unless a pill left the app's.
  const keepFilter = useRef(false);
  useEffect(() => {
    if (keepFilter.current) keepFilter.current = false;
    else setFilter(appKey);
  }, [appKey]);

  const apps = useMemo(() => railPanels(workbench), [workbench]);
  const artifacts = useMemo(() => libraryArtifacts(index.artifacts), [index.artifacts]);
  const provided = useMemo(() => pluginArtifacts(index.artifacts), [index.artifacts]);
  const categories = useMemo(
    () => libraryCategories([...artifacts, ...provided], apps.flatMap(appCategoryKeys)),
    [artifacts, provided, apps],
  );
  // The project of the conversation that made an artifact, when it is in one.
  const directory = useWorkspaceDirectory((state) => state);
  const placeOf = (artifact: ArtifactListingEntry) => {
    if (artifact.conversation === null) return null;
    const projectId =
      directory.placements[artifact.conversation]?.projectId ??
      directory.projects.find((project) => project.conversationId === artifact.conversation)?.id;
    return directory.projects.find((project) => project.id === projectId)?.name ?? null;
  };
  const ofKind = (artifact: ArtifactListingEntry) =>
    filter === null || filter === FROM_PLUGINS || categoryOf(artifact).key === filter;
  const shown =
    filter === FROM_PLUGINS
      ? []
      : artifacts.filter((artifact) => ofKind(artifact) && artifactMatches(artifact, query));
  // What the plugins provide of the kind chosen, by app.
  const providedShown = provided.filter(
    (artifact) => ofKind(artifact) && artifactMatches(artifact, query),
  );
  const groups = pluginGroups(providedShown, apps);
  // A search or a kind shows what matches in each app; otherwise each app is collapsed.
  const searching = query.trim() !== '';
  const groupsOpen = searching || (filter !== null && filter !== FROM_PLUGINS);

  // An artifact asked for by its id (`/artifacts?artifactId=`).
  useEffect(() => {
    if (requested === null) return;
    const artifact = index.artifacts.find((candidate) => candidate.id === requested);
    if (artifact === undefined) return;
    setRequested(null);
    // An artifact asked for by its id opens full screen, on its plugin's page.
    router.replace(appHomePath(artifact.plugin, artifact.id));
  }, [index.artifacts, requested, router]);
  useEffect(() => {
    if (initialArtifactId) setRequested(initialArtifactId);
  }, [initialArtifactId]);

  useEffect(() => {
    const settings = readAppSettings();
    applyAppSettings(settings);
    setTheme(resolveDesktopTheme(settings.theme));
  }, []);

  const choose = (key: string | null) => {
    setFilter(key);
    // Another kind than the app's is the Library's.
    if (panel !== null && (key === null || !appKeys.includes(key))) {
      keepFilter.current = true;
      router.replace(LIBRARY_PATH);
    }
  };
  // Outside a conversation an item opens full screen, on its plugin's page (its conversation is
  // one click away there: "From: <conversation>").
  const open = (artifact: ArtifactListingEntry) => openAppHome(artifact.plugin, artifact.id);
  // "Use" is Duplicate: the copy opens in a new conversation, its app beside it.
  const use = (artifact: ArtifactListingEntry) => {
    setUsing(artifact.id);
    void duplicateArtifact(artifact, 'new').then(
      () => setUsing(null),
      (error: unknown) => {
        setUsing(null);
        toast.error(
          `Use ${artifact.title}`,
          error instanceof Error ? error.message : String(error),
        );
      },
    );
  };

  const noun = panel === null ? null : appNoun(panel);
  // A list that could not be read says so once, with Retry: never "0 of your artifacts" too.
  const failed = index.error !== null && index.artifacts.length === 0;
  const counted = !failed && !(index.loading && index.artifacts.length === 0);
  const description = counted
    ? `${artifacts.length} of your artifacts · ${provided.length} from plugins`
    : 'Your artifacts and what your plugins provide';
  const emptyYours =
    noun !== null
      ? `No ${noun}s yet.`
      : provided.length > 0
        ? 'No artifacts yet. Start from a template below, or ask in a conversation.'
        : 'No artifacts yet: what your conversations make shows here.';

  return (
    <PluginWorkbenchContext.Provider value={workbench}>
      <main
        // Inside the shell, which already keeps clear of a phone's notch and home indicator: not a
        // second time (`.desktop-shell` pads by the safe areas).
        className="desktop-shell flex h-full min-h-0 flex-col overflow-hidden bg-background p-0! text-foreground"
        data-theme={theme}
      >
        <header className="flex shrink-0 flex-wrap items-start gap-3 px-10 pt-8 max-md:px-4 max-md:pt-3">
          <div className="min-w-0 flex-1 max-md:hidden">
            <h1 className="type-page-title truncate">Library</h1>
            <p className="type-meta mt-0.5 truncate text-muted-foreground">{description}</p>
          </div>
          <div className="relative w-72 max-md:w-full">
            <Search
              aria-hidden
              className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3.5 size-4 text-muted-foreground"
            />
            <Input
              aria-label={noun === null ? 'Search the library' : `Search ${noun}s`}
              className="h-control-md rounded-control border-transparent bg-control pl-10 shadow-none"
              onChange={(event) => setQuery(event.target.value)}
              placeholder={noun === null ? 'Search the library' : `Search ${noun}s`}
              value={query}
            />
          </div>
          {panel !== null && (
            // The app's own place is its home, not this filter.
            <Button onClick={() => openAppHome(panel.plugin)} type="button" variant="outline">
              Open {panel.title}
              <ArrowUpRight className="size-4" />
            </Button>
          )}
        </header>
        {categories.length > 0 && !failed && (
          <div
            aria-label="Kinds of artifacts"
            // The bottom padding leaves room for a chip's 44 px touch area (the row scrolls, so it clips).
            className="flex shrink-0 gap-1.5 overflow-x-auto px-10 pt-4 pb-1 max-md:px-4 pointer-coarse:pb-2"
            role="toolbar"
          >
            <FilterPill
              active={filter === null}
              count={artifacts.length + provided.length}
              label="All"
              onSelect={() => choose(null)}
            />
            {categories.map((category) => (
              <FilterPill
                active={filter === category.key}
                count={category.count}
                key={category.key}
                label={category.label}
                onSelect={() => choose(category.key)}
              />
            ))}
            {provided.length > 0 && (
              <FilterPill
                active={filter === FROM_PLUGINS}
                count={provided.length}
                label="From plugins"
                onSelect={() => choose(FROM_PLUGINS)}
              />
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-10 pt-3 pb-8 max-md:px-4">
          {failed ? (
            <InlineMessage
              actions={
                <Button onClick={index.refresh} size="sm" type="button" variant="outline">
                  Retry
                </Button>
              }
              title={
                noun === null ? "Couldn't load your artifacts." : `Couldn't load your ${noun}s.`
              }
              tone="error"
            >
              {index.error}
            </InlineMessage>
          ) : index.loading && index.artifacts.length === 0 ? null : searching &&
            shown.length === 0 &&
            providedShown.length === 0 ? (
            <p className="py-12 text-center text-muted-foreground text-sm">Nothing matches.</p>
          ) : (
            <div className="flex flex-col gap-8 max-md:pb-16">
              {filter !== FROM_PLUGINS && (
                <LibrarySection title={noun === null ? 'Your artifacts' : `Your ${noun}s`}>
                  {shown.length === 0 ? (
                    <p className="py-8 text-center text-muted-foreground text-sm">
                      {searching ? `None of your ${noun ?? 'artifact'}s match.` : emptyYours}
                    </p>
                  ) : (
                    <ul aria-label="Artifacts" className={LIBRARY_GRID}>
                      {/* One card per source, its renders under it. */}
                      {withRenders(shown, artifacts).map(({ artifact, renders }) => (
                        <LibraryCard
                          artifact={artifact}
                          glyph={artifactGlyph(artifact, workbench)}
                          key={artifact.id}
                          onOpen={open}
                          place={placeOf(artifact)}
                          preview={hasPreview(artifact, workbench)}
                          renders={renders}
                        />
                      ))}
                    </ul>
                  )}
                </LibrarySection>
              )}
              {providedShown.length > 0 && (
                <LibrarySection count={providedShown.length} title="From your plugins">
                  <PluginGroups
                    groups={groups}
                    onOpen={open}
                    onUse={use}
                    open={groupsOpen}
                    using={using}
                    workbench={workbench}
                  />
                </LibrarySection>
              )}
            </div>
          )}
        </div>
      </main>
    </PluginWorkbenchContext.Provider>
  );
}

function FilterPill({
  active,
  count,
  label,
  onSelect,
}: {
  active: boolean;
  count: number;
  label: string;
  onSelect(): void;
}) {
  return (
    <Chip onClick={onSelect} selected={active}>
      {label}
      <span className="type-caption text-subtle-foreground">{count}</span>
    </Chip>
  );
}
